/** @jsxImportSource @emotion/react */
import {
  Dispatch,
  SetStateAction,
  useEffect,
  PropsWithChildren,
  useState,
  Fragment,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';

import styles, { getBottomSafeAreaMargin } from './Toast.style';

import Spinner from '../Spinner';

import useWindowSize from '../../hooks/useWindowSize';
import { IS_SSR } from '../../../constants/common';

type ToastPositionType = 'bottom' | 'top';
type ToastColorType = 'success' | 'warning' | 'general' | 'error';
export const ToastPositions: ToastPositionType[] = ['bottom', 'top'];
export const ToastColors: ToastColorType[] = ['success', 'warning', 'general', 'error'];

export type PropsType = PropsWithChildren<{
  className?: string;
  style?: Record<string, string | number>;
  isOpen?: boolean;
  toggle?: Dispatch<SetStateAction<boolean>>;
  position?: 'bottom' | 'top';
  color?: 'success' | 'warning' | 'general' | 'error';
  loading?: boolean;
  timeoutMs?: 5000 | 10000;
  insertToBody?: boolean;
}>;

function Toast({
  style,
  className = '',
  children,
  isOpen = true,
  toggle,
  color = 'general',
  position = 'bottom',
  loading = false,
  timeoutMs = 5000,
  insertToBody = true,
}: PropsType) {
  const [isVisible, setVisible] = useState<boolean>(isOpen);
  const [isRemoved, setRemoved] = useState<boolean>(false);
  const [innerWidth] = useWindowSize();

  const width = useMemo(() => {
    return innerWidth > 768 ? '430px' : 'calc(100% - 32px)';
  }, []);

  useEffect(() => {
    if (!isVisible || !toggle) {
      return;
    }
    setTimeout(() => {
      toggle(false);
    }, timeoutMs);
  }, [isVisible, timeoutMs, toggle]);

  useEffect(() => {
    setTimeout(() => {
      setVisible(false);
    }, timeoutMs);
  }, [timeoutMs]);

  function handleTransitionEnd() {
    setRemoved(true);
  }

  useEffect(() => {
    setVisible(isOpen || false);
  }, [isOpen]);

  if (insertToBody) {
    const toast = (
      <div
        style={style}
        className={className}
        css={[
          styles.container(color, position, width),
          isOpen ? styles.toastShow : styles.toastHide,
          getBottomSafeAreaMargin(),
        ]}
        onTransitionEnd={handleTransitionEnd}
      >
        {loading && <Spinner css={styles.spinner} size={20} color="White" />}
        {children}
      </div>
    );

    return IS_SSR ? toast : createPortal(toast, document.body);
  }

  if (isRemoved) {
    return <Fragment />;
  }

  return (
    <div
      style={style}
      className={className}
      css={[
        styles.container(color, position, width),
        isVisible ? styles.toastShow : styles.toastHide,
        getBottomSafeAreaMargin(),
      ]}
      onTransitionEnd={handleTransitionEnd}
    >
      {loading && <Spinner css={styles.spinner} size={20} color="White" />}
      {children}
    </div>
  );
}

export default Toast;
